import React, { useEffect, useState, useCallback } from 'react';
import { StyleSheet, SafeAreaView, SectionList, useWindowDimensions, Pressable } from 'react-native';
import { Checkbox } from 'react-native-paper';
import { useTheme } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useSettingsContext } from '../../hooks/useSettingsContext';
import CustomLabel from '../../components/CustomLabel/CustomLabel';
import CustomButton from '../../components/CustomButton';
import { useSettings } from '../../hooks/useSettings';

const SettingsEmployeeListView = React.memo(
    ({ item, index, sectionTitle, styles, t, handleToggleVisibility }) => {
        return (
            <SafeAreaView style={styles.modalCheckBoxRow}>
                <CustomLabel value={item.window} type="CHECKBOXLABEL" />
                <Checkbox
                    status={item.isVisible ? "checked" : "unchecked"}
                    onPress={() => handleToggleVisibility(sectionTitle, item.window)}
                />
            </SafeAreaView>
        );
    }
);

const SettingsEmployeeScreen = (props) => {
    const { colors } = useTheme();
    const styles = makeStyles(colors);
    const { t } = useTranslation();
    const { width, height } = useWindowDimensions();
    const [sectionData, setSectionData] = useState([]);
    const [expandedSections, setExpandedSections] = useState(new Set());
    const { saveEmployeeTabs } = useSettings();
    const { employmentTabData, miscellaneousTabData, additionalTabData, personalTabData } = useSettingsContext();

    useEffect(() => {
        if (employmentTabData === undefined) return;

        let tempSectionData = [];
        tempSectionData.push({ title: 'Employment Tab', data: Object.entries(employmentTabData).map(([window, value]) => ({ window, ...value })) });
        tempSectionData.push({ title: 'Personal Data Tab', data: Object.entries(personalTabData).map(([window, value]) => ({ window, ...value })) });
        tempSectionData.push({ title: 'Additional Tab', data: Object.entries(additionalTabData).map(([window, value]) => ({ window, ...value })) });
        tempSectionData.push({ title: 'Miscellaneous Tab', data: Object.entries(miscellaneousTabData).map(([window, value]) => ({ window, ...value })) });

        setSectionData(tempSectionData);
    }, [employmentTabData, personalTabData, additionalTabData, miscellaneousTabData]);

    function handleToggle(title) {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(title)) {
                next.delete(title);
            } else {
                next.add(title);
            }
            return next;
        });
    }

    function handleToggleVisibility(sectionTitle, itemKey) {
        setSectionData(prevSections => {
            return prevSections.map(section => {
                if (section.title !== sectionTitle) return section;

                const updatedData = section.data.map(item => {
                    if (item.window === itemKey) {
                        return { ...item, isVisible: !item.isVisible };
                    }
                    return item;
                });

                return { ...section, data: updatedData };
            });
        });
    }
    const handleSubmit = () => {
        saveEmployeeTabs(sectionData).then(() => { console.log('Success') }).catch(console.error)
    }

    const renderFooter = () => (
        <CustomButton
            text={t('common:save')}
            onPress={() => handleSubmit()}
            type="BUTTON_SAVE"
            isGradient={true}
        />
    );
    const RenderSettingsEmloyeeList = useCallback(
        ({ item, index, sectionTitle }) => (
            <SettingsEmployeeListView
                item={item}
                index={index}
                sectionTitle={sectionTitle}
                styles={styles}
                t={t}
                handleToggleVisibility={handleToggleVisibility}
            />
        ),
        [styles, t, handleToggleVisibility]
    );

    return (
        <SafeAreaView style={styles.mainView}>
            <SectionList
                sections={sectionData}
                keyExtractor={(item, index) => `${item.key}-${index}`}
                initialNumToRender={30}
                contentContainerStyle={{
                    paddingHorizontal: 10,
                    maxHeight: height * 0.5,
                }}
                renderItem={({ section, item, index }) => (
                    expandedSections.has(section.title) ? (
                        <RenderSettingsEmloyeeList item={item} index={index} sectionTitle={section.title} />
                    ) : null
                )}
                renderSectionHeader={({ section: { title } }) => (
                    <Pressable onPress={() => handleToggle(title)} style={styles.pressableView}>
                        <CustomLabel value={title} type='EDIT_EMPLOYEE_EXPANDABLE' />
                    </Pressable>
                )}
                ListFooterComponent={renderFooter}
            />
        </SafeAreaView>
    );
}

const makeStyles = (colors) => StyleSheet.create({
    mainView: {
        flex: 1,
    },
    pressableView: {
        width: '100%',
        flexDirection: 'row',
    },
    modalCheckBoxRow: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
        justifyContent: 'space-between',
    },
});

export default SettingsEmployeeScreen;
